import { useEffect, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { X, Plus, ChatCircle, Warning, CaretLeft } from "@phosphor-icons/react"
import { Button } from "../../../components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../../components/ui/select"
import { cn } from "../../../lib/utils"
import { useGlobalAIStore } from "../stores/global-ai-store"
import { usePendingActionStore } from "../stores/pending-action-store"
import { useUIStore } from "../../../stores/ui-store"
import { useGlobalAISession } from "../hooks/useGlobalAISession"
import { useGlobalAIStream } from "../hooks/useGlobalAIStream"
import { GlobalAIChat } from "./GlobalAIChat"
import { GlobalAIInput, type GlobalAIInputHandle } from "./GlobalAIInput"
import { GlobalAIMatterTable } from "./GlobalAIMatterTable"
import type { MatterCard } from "../types"

export function GlobalAIDrawer() {
  const navigate = useNavigate()
  const { isOpen, setOpen, viewMode, setViewMode } = useGlobalAIStore()
  const { setPendingAction } = usePendingActionStore()
  const { selectedDivision } = useUIStore()

  const {
    sessions,
    activeSession,
    createSession,
    loadSessions,
    loadSession,
    setActiveSession,
    updateSessionName,
  } = useGlobalAISession()

  const {
    messages,
    isStreaming,
    currentText,
    currentToolCalls,
    matterCards,
    error,
    sendMessage,
    setMessages,
    cancelStream,
    clearMatterCards,
    restoreMatterCards,
  } = useGlobalAIStream({
    onSessionNameUpdate: (name) => {
      if (activeSession) {
        updateSessionName(activeSession._id, name)
      }
    },
  })

  const inputRef = useRef<GlobalAIInputHandle>(null)
  const justCreatedSessionRef = useRef(false)
  const wasStreamingRef = useRef(false)
  const prevSessionIdRef = useRef<string | null>(null)

  // Load sessions when drawer opens
  useEffect(() => {
    if (isOpen) {
      loadSessions()
    }
  }, [isOpen, loadSessions])

  // Focus input when drawer opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  // Focus input when streaming ends
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      inputRef.current?.focus()
    }
    wasStreamingRef.current = isStreaming
  }, [isStreaming])

  // Load messages only when switching to a different session
  useEffect(() => {
    const currentId = activeSession?._id || null
    const sessionChanged = currentId !== prevSessionIdRef.current
    prevSessionIdRef.current = currentId

    if (justCreatedSessionRef.current) {
      justCreatedSessionRef.current = false
      return
    }

    // Only sync when session ID changes AND not streaming
    // This prevents sync when: streaming ends, session renamed, or any other state change
    if (!sessionChanged || isStreaming) {
      return
    }

    if (activeSession) {
      setMessages(activeSession.messages || [])
      // Restore matter cards from session (or clear if none)
      restoreMatterCards(activeSession.matter_cards || null)
    } else {
      setMessages([])
      restoreMatterCards(null)
    }
  }, [activeSession, setMessages, isStreaming, restoreMatterCards])

  const handleNewConversation = async () => {
    if (!selectedDivision) return
    clearMatterCards()
    setViewMode("split")  // Reset to default view mode
    await createSession(selectedDivision)
  }

  const handleSendMessage = async (content: string) => {
    if (!selectedDivision) return
    if (!activeSession) {
      justCreatedSessionRef.current = true
      const newSession = await createSession(selectedDivision)
      if (newSession) {
        sendMessage(newSession._id, content)
      }
    } else {
      sendMessage(activeSession._id, content)
    }
  }

  const handleMatterCardClick = useCallback(
    (card: MatterCard) => {
      // Set the pending action for the matter AI to pick up
      if (card.pending_action) {
        setPendingAction(card.matter_id, card.pending_action)
      }

      // Navigate to the matter page
      navigate(`/matters/${card.matter_id}`)

      // Optionally close the global drawer
      // setOpen(false)
    },
    [navigate, setPendingAction]
  )

  // Don't render if closed
  if (!isOpen) {
    return null
  }

  // Determine panel visibility based on view mode
  const hasMatterCards = !!matterCards
  const showChat = !hasMatterCards || viewMode === "split" || viewMode === "chat"
  const showResults = hasMatterCards && (viewMode === "split" || viewMode === "results")
  const isWideDrawer = hasMatterCards && viewMode === "split"

  return (
    <div
      className={cn(
        "fixed inset-y-0 left-0 z-[9999]",
        "h-screen",
        "bg-modal text-modal-foreground",
        "border-r border-border",
        "shadow-[4px_0_24px_rgba(0,0,0,0.15)] dark:shadow-[2px_0_8px_rgba(0,0,0,0.6)]",
        "flex flex-col",
        "animate-in slide-in-from-left duration-200",
        "transition-[width] duration-300 ease-in-out",
        isWideDrawer ? "w-[750px] max-w-[95vw]" : "w-[420px] max-w-[90vw]"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/50">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <ChatCircle className="h-5 w-5 text-primary flex-shrink-0" />
          {sessions.length > 1 ? (
            <Select
              value={activeSession?._id || ""}
              onValueChange={(value) => {
                const session = sessions.find((s) => s._id === value)
                if (session) {
                  clearMatterCards()
                  setActiveSession(session)
                  loadSession(session._id)
                }
              }}
            >
              <SelectTrigger className="h-8 text-xs font-medium max-w-[220px]">
                <span className="truncate">
                  {activeSession?.name || "Global AI"}
                </span>
              </SelectTrigger>
              <SelectContent>
                {sessions.map((session) => (
                  <SelectItem key={session._id} value={session._id} className="text-xs">
                    {session.name} - {new Date(session.created_at * 1000).toLocaleDateString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="font-semibold text-sm truncate">
              {activeSession?.name || "Global AI"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={handleNewConversation}
            title="New conversation"
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setOpen(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Main content area - split pane */}
      <div className="flex-1 overflow-hidden flex">
        {/* Chat panel */}
        {showChat && (
          <div
            className={cn(
              "flex flex-col",
              "transition-all duration-200 ease-in-out overflow-hidden",
              "flex-1",
              showResults && "border-r border-border"
            )}
          >
            {/* Chat header - only show when we have matter cards (split view available) */}
            {hasMatterCards && (
              <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/50">
                <span className="text-xs font-medium text-muted-foreground truncate">
                  Conversation
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 flex-shrink-0"
                  onClick={() => setViewMode(viewMode === "split" ? "results" : "split")}
                  title={viewMode === "split" ? "Hide chat" : "Show results"}
                >
                  <CaretLeft className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}

            {/* Chat content */}
            <div className="flex-1 overflow-hidden">
              <GlobalAIChat
                messages={messages}
                isStreaming={isStreaming}
                currentText={currentText}
                currentToolCalls={currentToolCalls}
              />
            </div>
          </div>
        )}

        {/* Results panel */}
        {showResults && matterCards && (
          <GlobalAIMatterTable
            matterCards={matterCards}
            onCardClick={handleMatterCardClick}
            viewMode={viewMode}
            onToggleView={() => setViewMode(viewMode === "split" ? "chat" : "split")}
          />
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className="px-4 py-2 bg-destructive/10 text-destructive text-xs">{error}</div>
      )}

      {/* Input area */}
      <div className="border-t border-border px-3 pt-2.5 pb-1.5 bg-muted/30">
        <GlobalAIInput
          ref={inputRef}
          onSend={handleSendMessage}
          onCancel={cancelStream}
          isStreaming={isStreaming}
          disabled={!selectedDivision}
          placeholder={selectedDivision ? "Search matters, find activity..." : "Select a division to start"}
        />
      </div>

      {/* No division selected state */}
      {!selectedDivision && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center text-muted-foreground p-6">
            <Warning className="h-12 w-12 mx-auto mb-3 text-amber-500 opacity-70" />
            <p className="text-sm font-medium">No Division Selected</p>
            <p className="text-xs mt-1">Please select a division from the sidebar to use Global AI</p>
          </div>
        </div>
      )}

      {/* Empty state - show when no messages */}
      {selectedDivision && messages.length === 0 && !isStreaming && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center text-muted-foreground p-6">
            <ChatCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm font-medium">Global AI Assistant</p>
            <p className="text-xs mt-1">Search across all matters and communications</p>
            <p className="text-xs mt-3 text-muted-foreground/70">
              Try: "Show me anyone who called today"
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
